// src/pages/Menu/Vinculation/OtherCRM/OtherCRMBoard.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { RefreshCcw, Search, Calendar, Plus, Star, Building2, Landmark, Briefcase, Download, BarChart3, Filter, ChevronDown, ChevronUp, Crown, Tag, XCircle, Clock } from 'lucide-react';
import { Banner } from '../../../../components/common/Banner';
import TimelineFilter from '../../../../components/common/TimelineFilter';
import SectionTips from '../../../../components/common/SectionTips';
import TooltipHelper from '../../../../components/common/TooltipHelper';
import { otherCrmListSummary, otherCrmAnalytics, otherCrmUpdateEntity, otherCrmCompanyDetail, otherCrmGetRecentMeetings } from '../../../../services/otherCRMService';
import OtherCompanyCRMModal from './OtherCompanyCRMModal';
import CreateOtherEntityModal from './CreateOtherEntityModal';
import QuickCreateMeetingModalExternal from './QuickCreateMeetingModalExternal';
import { useAuth } from '../../../../context/authContext';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '../../../../components/common/ArrowBack';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const normalizeEntity = (c) => ({
  rut: c.rut ?? c.rutEmpresa ?? '',               // puede venir vacío, no se usa como clave
  name: c.trade_name?.trim() ? c.trade_name : (c.business_name || c.name || 'Sin nombre'),
  business_name: c.business_name ?? '',
  trade_name: c.trade_name ?? '',
  industry: c.industry ?? '',
  region: c.region ?? '',
  district: c.district ?? '',
  logo: c.logo ?? c.logo_url ?? null,
  meetingsCount: Number(c.meetingsCountYear ?? c.meetingsCount ?? 0),
  lastMeetingAt: c.lastMeetingAt ?? null,
  role: (c.role ?? '').toString().toLowerCase(),
  id: c.id ?? c.uuid ?? c._id ?? null,
  is_public: c.is_public ?? true,
  entity_type: c.entity_type ?? null,
  is_favorite: c.is_favorite ?? false,
});

const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const onlyDate = (iso) => {
  if (!iso) return '—';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const fmt = (n) => new Intl.NumberFormat('es-CL').format(Number.isFinite(n) ? n : 0);

// Componente de lista simplificado (sin semáforo)
const ListItem = ({ item, onOpen, onToggleFavorite, filterYear, yearFrom, monthFrom, yearTo, monthTo, isRecent }) => {
  const entityTypeLabel = item.entity_type === 'fundacion' ? 'Fundación' : 
                         item.entity_type === 'corporacion' ? 'Corporación' : '—';
  const publicLabel = item.is_public ? 'Pública' : 'Privada';

  // Verificar si lastMeetingAt corresponde al filtro aplicado
  const shouldShowLastMeeting = item.lastMeetingAt && (() => {
    const d = parseDateToLocal(item.lastMeetingAt);
    if (!d) return false;
    
    // Si hay rango de fechas, verificar que esté dentro del rango
    if (yearFrom && monthFrom && yearTo && monthTo) {
      const meetingYear = d.getFullYear();
      const meetingMonth = d.getMonth() + 1; // getMonth() es 0-indexed
      
      const fromYear = Number(yearFrom);
      const fromMonth = Number(monthFrom);
      const toYear = Number(yearTo);
      const toMonth = Number(monthTo);
      
      // Verificar si la fecha está dentro del rango
      if (meetingYear < fromYear || meetingYear > toYear) return false;
      if (meetingYear === fromYear && meetingMonth < fromMonth) return false;
      if (meetingYear === toYear && meetingMonth > toMonth) return false;
      
      return true;
    }
    
    // Si solo hay año, verificar que coincida
    if (filterYear) {
      return d.getFullYear() === Number(filterYear);
    }
    
    // Si no hay filtros, mostrar siempre
    return true;
  })();

  // Estilos para favoritos
  const favoriteStyles = item.is_favorite
    ? 'border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 shadow-lg'
    : 'border border-gray-200 bg-white';

  return (
    <div className={`group rounded-xl ${favoriteStyles} hover:shadow-md hover:border-[#0488AF]/30 transition-all duration-200 overflow-hidden`}>
      <div className="flex items-center gap-4 p-4">
        {/* Indicador de favorito */}
        <div className="flex-shrink-0 w-10 flex items-center justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite?.(item);
            }}
            className={`p-2 rounded-lg transition-colors ${
              item.is_favorite 
                ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100' 
                : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-50'
            }`}
            title={item.is_favorite ? 'Quitar como actor principal' : 'Marcar como actor principal'}
          >
            <Star className={`w-5 h-5 ${item.is_favorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Nombre */}
        <div className="flex-1 min-w-0 px-2">
          <button
            onClick={() => onOpen(item)}
            className="text-left w-full"
          >
            <div className="font-semibold text-base text-[#0b3a66] truncate group-hover:text-[#0488AF] transition-colors">
              {item.name || 'Sin nombre'}
            </div>
            {shouldShowLastMeeting && !isRecent && (
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Última reunión: {onlyDate(item.lastMeetingAt)}
              </div>
            )}
          </button>
        </div>

        {/* Público/Privada con icono */}
        <div className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-gray-50 text-sm font-medium text-gray-700 min-w-[120px] text-center inline-flex items-center justify-center gap-1.5">
          <Building2 className="w-4 h-4 flex-shrink-0" />
          <span className="whitespace-nowrap">{publicLabel}</span>
        </div>

        {/* Tipo de entidad con icono */}
        <div className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-blue-50 text-sm font-medium text-blue-700 min-w-[150px] text-center inline-flex items-center justify-center gap-1.5">
          {item.entity_type === 'fundacion' ? (
            <Landmark className="w-4 h-4 flex-shrink-0" />
          ) : item.entity_type === 'corporacion' ? (
            <Briefcase className="w-4 h-4 flex-shrink-0" />
          ) : (
            <Landmark className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="whitespace-nowrap">{entityTypeLabel}</span>
        </div>

        {/* Reuniones - simplificado sin semáforo */}
        <div className="flex-shrink-0 inline-flex flex-col items-center justify-center px-4 py-2 rounded-xl bg-[#0488AF]/10 border border-[#0488AF]/30 text-center min-w-[90px]">
          <span className="text-lg font-bold leading-none text-[#0488AF]">{item.meetingsCount}</span>
          <span className="text-[10px] font-medium uppercase leading-tight mt-0.5 text-[#0488AF] whitespace-nowrap">
            {item.meetingsCount === 1 ? 'reunión' : 'reuniones'}
          </span>
        </div>

      </div>
    </div>
  );
};

export default function OtherCRMBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const myRutRaw = (user?.rut || localStorage.getItem('rut') || '').toString();
  const myRut = myRutRaw.replace(/\./g, '').replace(/-/g, '').trim();

  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState('');

  // Año seleccionado y opciones dinámicas
  const [year, setYear] = useState('');      // '', '2025', etc.
  const [yearOptions, setYearOptions] = useState(['']); // se llena con discoverYears()
  const discoveringRef = useRef(false);

  // Mes seleccionado (1-12, solo activo cuando hay año)
  const [month, setMonth] = useState('');     // '', '1', '2', ..., '12'

  // Rango de fechas (desde/hasta)
  const [yearFrom, setYearFrom] = useState('');
  const [monthFrom, setMonthFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [monthTo, setMonthTo] = useState('');

  // Filtros nuevos
  const [isPublicFilter, setIsPublicFilter] = useState(''); // '' | 'true' | 'false'
  const [favoriteFilter, setFavoriteFilter] = useState(''); // '' | 'true' | 'false'
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sortOrder, setSortOrder] = useState(''); // '' | 'desc' | 'asc' - ordenar por cantidad de reuniones

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [initialMeetingId, setInitialMeetingId] = useState(null);

  // Modal para crear nueva entidad
  const [openCreateEntity, setOpenCreateEntity] = useState(false);
  
  // Modal para editar entidad
  const [openEditEntity, setOpenEditEntity] = useState(false);
  const [entityToEdit, setEntityToEdit] = useState(null);

  // Modal para crear reunión rápida
  const [openQuickCreate, setOpenQuickCreate] = useState(false);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const openModal = (item, meetingId = null) => { 
    setSelected(item); 
    setInitialMeetingId(meetingId);
    setOpen(true); 
  };
  const closeModal = () => { 
    setOpen(false); 
    setSelected(null); 
    setInitialMeetingId(null);
  };
  
  // Función para abrir reunión desde el banner
  const openMeetingFromBanner = async (meeting) => {
    const entityId = meeting.entityId || meeting.entity?.id;
    if (!entityId) return;
    
    // Buscar la entidad en la lista
    const entity = raw.find(e => String(e.id) === String(entityId));
    
    if (entity) {
      openModal(entity, meeting.id);
    } else {
      // Si no se encuentra la entidad, preguntar si quiere crear una nueva
      const result = await Swal.fire({
        title: 'Entidad no encontrada',
        html: `La entidad asociada a esta reunión no existe en el sistema.<br/><br/>¿Deseas crear una nueva entidad?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, crear entidad',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        // Abrir el modal de crear nueva entidad
        setOpenCreateEntity(true);
      }
    }
  };

  // Cargar lista según filtros
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      // Si hay rango de fechas (desde/hasta), usar rango
      if (yearFrom && monthFrom && yearTo && monthTo) {
        params.yearFrom = Number(yearFrom);
        params.monthFrom = Number(monthFrom);
        params.yearTo = Number(yearTo);
        params.monthTo = Number(monthTo);
      } else if (year) {
        // Si hay año individual, usar filtro de año/mes
        params.year = Number(year);
        if (month && year) {
          // Si month tiene formato "1-9" (rango), parsearlo
          const monthParts = month.split('-');
          if (monthParts.length === 2) {
            // Rango de meses: "1-9" significa enero a septiembre
            params.monthFrom = Number(monthParts[0]);
            params.monthTo = Number(monthParts[1]);
          } else {
            // Mes individual
            params.month = Number(month);
          }
        }
      }
      if (isPublicFilter !== '') params.is_public = isPublicFilter === 'true';

      const list = await otherCrmListSummary(Object.keys(params).length > 0 ? params : undefined);
      let norm = (Array.isArray(list) ? list : []).map(normalizeEntity);

      // excluir admins/propia entidad Cidere
      norm = norm.filter((c) => {
        const rutNorm = (c.rut || '').toString().replace(/\./g, '').replace(/-/g, '').trim();
        const isMine = !!myRut && rutNorm === myRut;
        const looksOwner = (c.trade_name || c.name || '').toString().toLowerCase().includes('cidere');
        return c.role !== 'admin' && !isMine && !looksOwner;
      });

      // Ordenar: favoritos primero
      norm.sort((a, b) => {
        if (a.is_favorite && !b.is_favorite) return -1;
        if (!a.is_favorite && b.is_favorite) return 1;
        return 0;
      });

      setRaw(norm);
    } catch (e) {
      setRaw([]);
      setError(e?.message || 'No se pudo cargar.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar reuniones recientes
  const loadRecentMeetings = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const meetings = await otherCrmGetRecentMeetings(10);
      setRecentMeetings(Array.isArray(meetings) ? meetings : []);
    } catch (e) {
      console.error('Error al cargar reuniones recientes:', e);
      setRecentMeetings([]);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  // Descubre años con actividad
  const discoverYears = async () => {
    if (discoveringRef.current) return;
    discoveringRef.current = true;

    const now = new Date().getFullYear();
    const minYear = now - 12;
    const maxYear = now + 3;
    const candidates = [];
    for (let y = minYear; y <= maxYear; y++) candidates.push(y);

    try {
      const results = await Promise.allSettled(
        candidates.map((y) => otherCrmAnalytics({ year: y }))
      );

      const yearsWithData = candidates.filter((y, i) => {
        const r = results[i];
        if (r.status !== 'fulfilled') return false;
        const meetings = Number(r.value?.totals?.meetings ?? 0);
        return meetings > 0;
      });

      const latestWithData = yearsWithData.length ? Math.max(...yearsWithData) : now;
      const opts = Array.from(new Set(['', ...yearsWithData, now]))
        .filter((x) => x === '' || Number.isFinite(x))
        .sort((a, b) => (a === '' ? 1 : b === '' ? -1 : b - a))
        .map(String);

      setYearOptions(opts);
      // Usar el año actual por defecto, no el último año con datos
      if (!year) setYear(String(now));
    } catch {
      const ys = new Set(
        raw
          .map((x) => {
            if (!x.lastMeetingAt) return null;
            const d = parseDateToLocal(x.lastMeetingAt);
            return d ? d.getFullYear() : null;
          })
          .filter(Boolean)
      );
      const currentYear = new Date().getFullYear();
      ys.add(currentYear);
      const opts = [''].concat([...ys].sort((a, b) => b - a)).map(String);
      setYearOptions(opts);
      // Usar el año actual por defecto, no el año máximo encontrado
      if (!year) setYear(String(currentYear));
    } finally {
      discoveringRef.current = false;
    }
  };

  // Handlers para acciones
  const handleToggleFavorite = async (item) => {
    const isCurrentlyFavorite = item.is_favorite;
    const action = isCurrentlyFavorite ? 'quitar' : 'marcar';
    const actionText = isCurrentlyFavorite ? 'Quitar como actor principal' : 'Marcar como actor principal';
    
    const result = await Swal.fire({
      title: actionText,
      text: isCurrentlyFavorite 
        ? `¿Estás seguro de quitar "${item.name || 'esta entidad'}" como actor principal?`
        : `¿Deseas marcar "${item.name || 'esta entidad'}" como actor principal?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isCurrentlyFavorite ? 'Sí, quitar' : 'Sí, marcar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
    });

    if (!result.isConfirmed) {
      return; // El usuario canceló
    }

    try {
      await otherCrmUpdateEntity(item.id, { is_favorite: !item.is_favorite });
      setRaw((prev) =>
        prev.map((e) => (e.id === item.id ? { ...e, is_favorite: !e.is_favorite } : e))
      );
      
      Swal.fire({
        icon: 'success',
        title: isCurrentlyFavorite ? 'Quitado de actores principales' : 'Marcado como actor principal',
        text: isCurrentlyFavorite 
          ? `"${item.name || 'La entidad'}" ya no es un actor principal.`
          : `"${item.name || 'La entidad'}" ahora es un actor principal.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire('Error', err?.message || 'No se pudo actualizar el favorito', 'error');
    }
  };

  const handleEdit = (item) => {
    setEntityToEdit(item);
    setOpenEditEntity(true);
  };

  // Efectos
  useEffect(() => { load(); /* eslint-disable-line */ }, [myRut, year, month, yearFrom, monthFrom, yearTo, monthTo, isPublicFilter]);
  useEffect(() => { loadRecentMeetings(); }, [loadRecentMeetings]); // Cargar reuniones recientes al montar
  
  // Escuchar eventos globales para actualizar reuniones recientes
  useEffect(() => {
    const handleMeetingCreated = () => {
      // Agregar un pequeño delay para asegurar que el backend haya procesado la nueva reunión
      setTimeout(() => {
        loadRecentMeetings();
      }, 500);
    };
    window.addEventListener('meetingCreated', handleMeetingCreated);
    window.addEventListener('meetingUpdated', handleMeetingCreated);
    window.addEventListener('meetingDeleted', handleMeetingCreated);
    return () => {
      window.removeEventListener('meetingCreated', handleMeetingCreated);
      window.removeEventListener('meetingUpdated', handleMeetingCreated);
      window.removeEventListener('meetingDeleted', handleMeetingCreated);
    };
  }, [loadRecentMeetings]);
  useEffect(() => { discoverYears(); /* eslint-disable-line */ }, []); // al montar
  useEffect(() => { if (!discoveringRef.current) discoverYears(); /* eslint-disable-line */ }, [raw.length]);
  
  // Resetear mes cuando cambia el año
  useEffect(() => { setMonth(''); }, [year]);

  // Función para exportar a Excel (datos históricos con filtros de tiempo si están aplicados)
  const handleExportToExcel = async () => {
    try {
      // Mostrar confirmación con información
      const confirmResult = await Swal.fire({
        title: '¿Exportar histórico de reuniones?',
        html: `
          <div class="text-left">
            <p class="mb-3">Se exportará el <strong>histórico completo de reuniones de entidades externas</strong> según los filtros aplicados.</p>
            <p class="text-sm text-gray-600">El archivo Excel incluirá todas las reuniones de entidades externas en el período seleccionado. Esto puede tardar unos minutos si hay muchas entidades.</p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, exportar',
        cancelButtonText: 'Cancelar',
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      // Mostrar modal de carga
      const loadingToast = Swal.fire({
        title: 'Exportando histórico de reuniones...',
        html: 'Generando archivo Excel con el histórico completo de reuniones de entidades externas según los filtros aplicados. Esto puede tardar unos minutos si hay muchas entidades. Por favor espera.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      setLoading(true);
      
      // Determinar rango de años según filtros
      let finalStartYear, endYear, years;
      let periodTitle = '';
      
      if (yearFrom && monthFrom && yearTo && monthTo) {
        // Usar filtros de rango completo
        finalStartYear = Number(yearFrom);
        endYear = Number(yearTo);
        years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        periodTitle = ` (${monthNames[Number(monthFrom) - 1]} ${yearFrom} - ${monthNames[Number(monthTo) - 1]} ${yearTo})`;
      } else if (yearFrom && yearTo) {
        // Usar filtros de rango de años
        finalStartYear = Number(yearFrom);
        endYear = Number(yearTo);
        years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
        periodTitle = ` (${yearFrom} - ${yearTo})`;
      } else if (year) {
        // Usar año seleccionado
        finalStartYear = Number(year);
        endYear = Number(year);
        years = [Number(year)];
        periodTitle = ` (${year})`;
      } else {
        // Sin filtros, usar comportamiento por defecto
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - 15;
        endYear = currentYear;
        finalStartYear = endYear - startYear > 20 ? endYear - 20 : startYear;
        years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
      }
      
      // Cargar todos los años históricos con conteo de entidades únicas (en paralelo)
      const allHistoricalData = await Promise.all(
        years.map(async (y) => {
          try {
            // Hacer ambas llamadas en paralelo para cada año
            const [analyticsData, entitiesData] = await Promise.allSettled([
              otherCrmAnalytics({ year: y }),
              otherCrmListSummary({ year: y })
            ]);
            
            const data = analyticsData.status === 'fulfilled' ? analyticsData.value : null;
            const entities = entitiesData.status === 'fulfilled' ? entitiesData.value : [];
            
            // Obtener entidades únicas con reuniones en este año
            const uniqueEntities = Array.isArray(entities) 
              ? entities.filter(e => {
                  const count = Number(e.meetingsCountYear ?? e.meetingsCount ?? 0);
                  return count > 0;
                }).length
              : 0;
            
            return { 
              label: String(y),
              period: String(y),
              count: Number(data?.totals?.meetings ?? 0),
              uniqueEntities: uniqueEntities
            };
          } catch (e) {
            console.error(`Error loading year ${y}:`, e);
            return { 
              label: String(y),
              period: String(y),
              count: 0,
              uniqueEntities: 0
            };
          }
        })
      );
      
      // Cargar datos históricos por meses (respetando filtros de mes si están aplicados)
      const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      
      // Determinar rango de meses según filtros
      let monthStart = 1;
      let monthEnd = 12;
      
      if (yearFrom && monthFrom && yearTo && monthTo) {
        // Si hay filtros de mes, usarlos solo para el primer y último año
        monthStart = Number(monthFrom);
        monthEnd = Number(monthTo);
      }
      
      // Preparar todas las llamadas mensuales en paralelo
      const monthlyPromises = [];
      for (const y of years) {
        // Determinar qué meses cargar para este año
        let startMonth = 1;
        let endMonth = 12;
        
        if (yearFrom && monthFrom && yearTo && monthTo) {
          if (y === finalStartYear) {
            // Primer año: desde monthFrom
            startMonth = Number(monthFrom);
            endMonth = 12;
          } else if (y === endYear) {
            // Último año: hasta monthTo
            startMonth = 1;
            endMonth = Number(monthTo);
          }
          // Años intermedios: todos los meses
        }
        
        for (let m = startMonth; m <= endMonth; m++) {
          // Calcular fechas de inicio y fin del mes (usando UTC para evitar problemas de zona horaria)
          const dateFrom = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0));
          const dateTo = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)); // Último día del mes
          
          // Formatear fechas como YYYY-MM-DD
          const startDate = dateFrom.toISOString().split('T')[0];
          const endDate = dateTo.toISOString().split('T')[0];
          
          // Crear promesa para este mes (hacer ambas llamadas en paralelo)
          const monthPromise = Promise.allSettled([
            otherCrmAnalytics({ startDate: startDate, endDate: endDate }),
            otherCrmListSummary({ year: y, month: m })
          ]).then(([analyticsResult, entitiesResult]) => {
            const data = analyticsResult.status === 'fulfilled' ? analyticsResult.value : null;
            const entities = entitiesResult.status === 'fulfilled' ? entitiesResult.value : [];
            
            const count = Number(data?.totals?.meetings ?? 0);
            const uniqueEntities = Array.isArray(entities) ? entities.length : 0;
            
            return {
              year: String(y),
              month: m,
              monthName: monthNames[m - 1],
              label: `${monthNames[m - 1]} ${y}`,
              period: `${y}-${String(m).padStart(2, '0')}`,
              count: count,
              uniqueEntities: uniqueEntities
            };
          }).catch((e) => {
            console.error(`Error loading ${y}-${m}:`, e);
            return {
              year: String(y),
              month: m,
              monthName: monthNames[m - 1],
              label: `${monthNames[m - 1]} ${y}`,
              period: `${y}-${String(m).padStart(2, '0')}`,
              count: 0,
              uniqueEntities: 0
            };
          });
          
          monthlyPromises.push(monthPromise);
        }
      }
      
      // Ejecutar todas las llamadas mensuales en paralelo (en lotes para no sobrecargar)
      const allMonthlyData = [];
      const batchSize = 20; // Procesar 20 meses a la vez
      for (let i = 0; i < monthlyPromises.length; i += batchSize) {
        const batch = monthlyPromises.slice(i, i + batchSize);
        // Actualizar progreso durante procesamiento de meses
        if (loadingToast && loadingToast.isVisible && i + batchSize < monthlyPromises.length) {
          const progress = Math.round(((i + batchSize) / monthlyPromises.length) * 100);
          loadingToast.update({
            html: `Exportando histórico de reuniones de entidades externas...<br/>Procesando meses: ${Math.min(i + batchSize, monthlyPromises.length)} de ${monthlyPromises.length} (${progress}%)`
          });
        }
        const results = await Promise.all(batch);
        allMonthlyData.push(...results);
      }
      
      setLoading(false);
      
      const workbook = new ExcelJS.Workbook();
      
      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0488AF' }
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF09274A' } },
        alignment: { horizontal: 'left', vertical: 'middle' }
      };
      
      const dataStyle = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        }
      };
      
      const fmt = (n) => new Intl.NumberFormat('es-CL').format(Number.isFinite(n) ? n : 0);
      const num = (v, def = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : def;
      };
      
      // Hoja 1: Datos históricos principales (por año)
      const mainSheet = workbook.addWorksheet('Datos Históricos');
      
      // Título
      mainSheet.mergeCells('A1:C1');
      mainSheet.getCell('A1').value = `Reuniones por año (Datos Históricos)${periodTitle}`;
      mainSheet.getCell('A1').style = titleStyle;
      mainSheet.getRow(1).height = 25;
      
      // Resumen
      const totalMeetings = allHistoricalData.reduce((sum, d) => sum + num(d.count, 0), 0);
      
      mainSheet.mergeCells('A2:C2');
      mainSheet.getCell('A2').value = `Total de reuniones: ${fmt(totalMeetings)}`;
      mainSheet.getRow(2).height = 20;
      
      // Espacio
      mainSheet.getRow(3).height = 10;
      
      // Encabezados
      mainSheet.getCell('A4').value = 'Año';
      mainSheet.getCell('B4').value = 'Cantidad de Reuniones';
      mainSheet.getCell('C4').value = 'Entidades Diferentes';
      mainSheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      mainSheet.getRow(4).height = 25;
      
      // Datos
      allHistoricalData.forEach(({ label, count, uniqueEntities }, index) => {
        const row = mainSheet.getRow(index + 5);
        row.getCell(1).value = label;
        row.getCell(2).value = num(count, 0);
        row.getCell(2).numFmt = '#,##0';
        row.getCell(3).value = num(uniqueEntities, 0);
        row.getCell(3).numFmt = '#,##0';
        
        row.eachCell((cell) => {
          cell.style = dataStyle;
        });
        row.height = 20;
      });
      
      // Ajustar ancho de columnas
      mainSheet.getColumn(1).width = 20;
      mainSheet.getColumn(2).width = 25;
      mainSheet.getColumn(3).width = 25;
      
      // Hoja 2: Histórico desglosado por meses
      const monthlySheet = workbook.addWorksheet('Histórico por Meses');
      
      // Título
      monthlySheet.mergeCells('A1:C1');
      monthlySheet.getCell('A1').value = `Reuniones por mes (Datos Históricos)${periodTitle}`;
      monthlySheet.getCell('A1').style = titleStyle;
      monthlySheet.getRow(1).height = 25;
      
      // Resumen
      const totalMonthlyMeetings = allMonthlyData.reduce((sum, d) => sum + num(d.count, 0), 0);
      
      monthlySheet.mergeCells('A2:C2');
      monthlySheet.getCell('A2').value = `Total de reuniones: ${fmt(totalMonthlyMeetings)}`;
      monthlySheet.getRow(2).height = 20;
      
      // Espacio
      monthlySheet.getRow(3).height = 10;
      
      // Encabezados
      monthlySheet.getCell('A4').value = 'Año';
      monthlySheet.getCell('B4').value = 'Mes';
      monthlySheet.getCell('C4').value = 'Cantidad de Reuniones';
      monthlySheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      monthlySheet.getRow(4).height = 25;
      
      // Datos
      allMonthlyData.forEach(({ year, monthName, count }, index) => {
        const row = monthlySheet.getRow(index + 5);
        row.getCell(1).value = year;
        row.getCell(2).value = monthName;
        row.getCell(3).value = num(count, 0);
        row.getCell(3).numFmt = '#,##0';
        
        row.eachCell((cell) => {
          cell.style = dataStyle;
        });
        row.height = 20;
      });
      
      // Ajustar ancho de columnas
      monthlySheet.getColumn(1).width = 15;
      monthlySheet.getColumn(2).width = 20;
      monthlySheet.getColumn(3).width = 25;
      
      // Función para formatear fechas
      const formatDate = (iso) => {
        if (!iso) return '—';
        try {
          const d = new Date(iso);
          if (isNaN(d.getTime())) return '—';
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          return `${day}-${month}-${year}`;
        } catch {
          return '—';
        }
      };
      
      // Hoja 3: Detalle completo de reuniones
      const detailSheet = workbook.addWorksheet('Detalle de Reuniones');
      
      // Título (siempre completo, sin filtros)
      detailSheet.mergeCells('A1:F1');
      detailSheet.getCell('A1').value = 'Detalle completo de reuniones (Datos Históricos Completos)';
      detailSheet.getCell('A1').style = titleStyle;
      detailSheet.getRow(1).height = 25;
      
      // Espacio
      detailSheet.getRow(2).height = 10;
      
      // Encabezados
      detailSheet.getCell('A3').value = 'Año';
      detailSheet.getCell('B3').value = 'Mes';
      detailSheet.getCell('C3').value = 'Entidad';
      detailSheet.getCell('D3').value = 'Fecha';
      detailSheet.getCell('E3').value = 'Número de participantes';
      detailSheet.getCell('F3').value = 'Categoría';
      detailSheet.getRow(3).eachCell((cell) => {
        cell.style = headerStyle;
      });
      detailSheet.getRow(3).height = 25;
      
      // Cargar todas las entidades y sus reuniones (SIEMPRE completo, sin filtros)
      try {
        const entities = await otherCrmListSummary(undefined);
        const allMeetings = [];
        
        // Para la hoja de detalle, siempre usar el rango completo histórico (últimos 15-20 años)
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - 15;
        const endYear = currentYear;
        const finalStartYearDetail = endYear - startYear > 20 ? endYear - 20 : startYear;
        
        // Función auxiliar para procesar una entidad
        const processEntity = async (entity) => {
          try {
            const entityId = entity.id || entity.uuid || entity._id;
            if (!entityId) return [];
            
            const detail = await otherCrmCompanyDetail(entityId);
            const meetings = Array.isArray(detail?.meetings) ? detail.meetings : [];
            
            // Filtrar reuniones solo por el rango histórico completo (sin filtros del usuario)
            const filteredMeetings = meetings.filter((m) => {
              if (!m.date) return false;
              const meetingYear = parseInt(m.date.split('-')[0]);
              return meetingYear >= finalStartYearDetail && meetingYear <= endYear;
            });
            
            // Nombre de la entidad
            const entityName = entity.trade_name || entity.business_name || entity.name || String(entityId) || '—';
            
            // Procesar cada reunión
            return filteredMeetings.map((meeting) => {
              if (!meeting.date) return null;
              const dateParts = meeting.date.split('-');
              if (dateParts.length !== 3) return null;
              
              const meetingYear = parseInt(dateParts[0]);
              const meetingMonth = parseInt(dateParts[1]);
              const meetingDate = meeting.date;
              
              // Contar participantes (si existe el campo)
              const participants = Array.isArray(meeting.participants) ? meeting.participants : [];
              const participantsCount = participants.length;
              
              // Obtener categoría
              const category = meeting.category || '—';
              
              return {
                year: meetingYear,
                month: meetingMonth,
                monthName: monthNames[meetingMonth - 1] || '',
                entityName: entityName,
                date: meetingDate,
                participantsCount: participantsCount,
                category: category,
              };
            }).filter(Boolean); // Filtrar nulls
          } catch (e) {
            console.error(`Error loading meetings for entity ${entity.id}:`, e);
            return [];
          }
        };
        
        // Procesar entidades en paralelo con límite de concurrencia (aumentado a 20 para mayor velocidad)
        const entitiesArray = Array.isArray(entities) ? entities : [];
        const batchSize = 20; // Aumentado de 10 a 20 para procesar más rápido
        
        for (let i = 0; i < entitiesArray.length; i += batchSize) {
          const batch = entitiesArray.slice(i, i + batchSize);
          const results = await Promise.allSettled(batch.map(processEntity));
          
          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              allMeetings.push(...result.value);
            }
          });
          
          // Actualizar progreso sin crear nuevo modal (más eficiente)
          if (loadingToast && loadingToast.isVisible && i + batchSize < entitiesArray.length) {
            const progress = Math.round(((i + batchSize) / entitiesArray.length) * 100);
            loadingToast.update({
              html: `Exportando histórico de reuniones de entidades externas...<br/>Procesando entidades: ${Math.min(i + batchSize, entitiesArray.length)} de ${entitiesArray.length} (${progress}%)`
            });
          }
        }
        
        // Ordenar por año, mes y fecha
        allMeetings.sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          if (a.month !== b.month) return a.month - b.month;
          return a.date.localeCompare(b.date);
        });
        
        // Agregar datos a la hoja
        allMeetings.forEach((meeting, index) => {
          const row = detailSheet.getRow(index + 4);
          row.getCell(1).value = meeting.year;
          row.getCell(2).value = meeting.monthName;
          row.getCell(3).value = meeting.entityName;
          row.getCell(4).value = formatDate(meeting.date);
          row.getCell(5).value = meeting.participantsCount;
          row.getCell(6).value = meeting.category;
          
          row.getCell(1).numFmt = '#,##0';
          row.getCell(5).numFmt = '#,##0';
          
          row.eachCell((cell) => {
            cell.style = {
              ...dataStyle,
              alignment: { horizontal: cell.col === 3 || cell.col === 6 ? 'left' : 'center', vertical: 'middle' },
            };
          });
          row.height = 20;
        });
        
        // Ajustar ancho de columnas
        detailSheet.getColumn(1).width = 12;
        detailSheet.getColumn(2).width = 15;
        detailSheet.getColumn(3).width = 35;
        detailSheet.getColumn(4).width = 15;
        detailSheet.getColumn(5).width = 18;
        detailSheet.getColumn(6).width = 30;
      } catch (error) {
        console.error('Error loading meeting details:', error);
        detailSheet.getCell('A4').value = 'Error al cargar los detalles de las reuniones';
        detailSheet.getCell('A4').style = { font: { italic: true, color: { argb: 'FFFF0000' } } };
      }
      
      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const fileName = `Relacionamiento_Entidades_Externas_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      // Cerrar modal de carga
      loadingToast.close();

      Swal.fire('Exportado', 'Los datos se han exportado correctamente a Excel.', 'success');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      // Cerrar modal de carga en caso de error
      if (typeof loadingToast !== 'undefined' && loadingToast.close) {
        loadingToast.close();
      }
      setLoading(false);
      Swal.fire('Error', 'Error al exportar los datos a Excel. Por favor, intenta nuevamente.', 'error');
    }
  };

  const industryOptions = useMemo(() => {
    const set = new Set(raw.map((x) => (x.industry || '').trim()).filter(Boolean));
    return [''].concat([...set].sort((a, b) => a.localeCompare(b, 'es')));
  }, [raw]);

  // Identificar la entidad con la reunión más reciente (solo la #1, no las top 3)
  const entitiesWithRecentMeetings = useMemo(() => {
    if (!recentMeetings || recentMeetings.length === 0) return { ids: new Set(), names: new Set() };
    
    const ids = new Set();
    const names = new Set();
    
    // Solo considerar la reunión #1 más reciente (no las top 3)
    const mostRecentMeeting = recentMeetings[0];
    
    if (mostRecentMeeting) {
      // Intentar obtener ID de diferentes campos posibles
      const entityId = mostRecentMeeting.entityId || mostRecentMeeting.entity?.id || null;
      if (entityId) {
        ids.add(String(entityId));
      }
      // Intentar obtener nombre de diferentes campos posibles
      const entityName = mostRecentMeeting.entityName || mostRecentMeeting.entity?.name || mostRecentMeeting.entity?.trade_name || mostRecentMeeting.entity?.business_name || null;
      if (entityName) {
        const normalizedName = String(entityName).trim().toLowerCase();
        names.add(normalizedName);
      }
    }
    
    return { ids, names };
  }, [recentMeetings]);

  // Verificar si una entidad tiene reunión reciente
  const hasRecentMeeting = useMemo(() => {
    return (entity) => {
      if (entitiesWithRecentMeetings.ids.size === 0 && entitiesWithRecentMeetings.names.size === 0) return false;
      
      const entityId = String(entity.id || '');
      const normalizedName = String(entity.name || '').trim().toLowerCase();
      const normalizedBusinessName = String(entity.business_name || '').trim().toLowerCase();
      const normalizedTradeName = String(entity.trade_name || '').trim().toLowerCase();
      
      const hasId = entityId && entitiesWithRecentMeetings.ids.has(entityId);
      const hasName = normalizedName && entitiesWithRecentMeetings.names.has(normalizedName);
      const hasBusinessName = normalizedBusinessName && entitiesWithRecentMeetings.names.has(normalizedBusinessName);
      const hasTradeName = normalizedTradeName && entitiesWithRecentMeetings.names.has(normalizedTradeName);
      
      return hasId || hasName || hasBusinessName || hasTradeName;
    };
  }, [entitiesWithRecentMeetings]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let result = raw.filter((c) => {
      const matchText =
        !ql ||
        c.name.toLowerCase().includes(ql) ||
        c.business_name?.toLowerCase().includes(ql) ||
        c.rut?.toLowerCase().includes(ql) ||
        c.region?.toLowerCase().includes(ql) ||
        c.district?.toLowerCase().includes(ql);
      const matchInd = !industry || c.industry === industry;
      const matchFavorite = favoriteFilter === '' || (favoriteFilter === 'true' && c.is_favorite) || (favoriteFilter === 'false' && !c.is_favorite);
      return matchText && matchInd && matchFavorite;
    });
    
    // Ordenar por cantidad de reuniones si hay ordenamiento seleccionado
    if (sortOrder === 'desc') {
      result = [...result].sort((a, b) => b.meetingsCount - a.meetingsCount);
    } else if (sortOrder === 'asc') {
      result = [...result].sort((a, b) => a.meetingsCount - b.meetingsCount);
    }
    
    return result;
  }, [raw, q, industry, favoriteFilter, sortOrder]);

  // Top 3 entidades con más reuniones
  const top3 = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => b.meetingsCount - a.meetingsCount);
    return sorted.slice(0, 3).filter(item => item.meetingsCount > 0);
  }, [filtered]);

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner title="Relacionamiento con entidades externas" message="N° de reuniones y minutas" />
      </div>

      <SectionTips
        section="el relacionamiento con entidades externas"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo gestionar el relacionamiento con entidades externas?"
        tipsList={[
          'Puedes crear nuevas entidades externas usando el botón "Nueva entidad".',
          'Busca entidades por nombre, RUT, región o comuna usando el campo de búsqueda.',
          'Filtra por tipo de entidad (Público/Privado) y rubro para encontrar entidades específicas.',
          'Marca entidades como favoritas para acceso rápido.',
          'Haz clic en una entidad para ver sus detalles, reuniones y minutas.',
          'Puedes crear reuniones y subir minutas desde el detalle de cada entidad.',
          'Utiliza los filtros de fecha para analizar períodos específicos.',
        ]}
        finalNote="Consejo: Mantén un registro actualizado de las reuniones y minutas para tener un vínculo preciso con cada entidad."
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766418233/Video_Tutorial_m%C3%B3dulo_Vinculaci%C3%B3n_Empresarial_-_Relacionamiento_con_Entidades_Externas_e8z0u8.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412955/Manual_de_usuario_m%C3%B3dulo_de_vinculaci%C3%B3n_empresarial_af2jv3.pdf"
      />

      {/* Card destacado para crear reunión */}
      <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">Gestiona tus reuniones</h3>
                  <p className="text-sm md:text-base text-gray-600 mt-1">
                    Crea y registra reuniones con entidades externas de forma rápida y sencilla
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpenQuickCreate(true)}
              className="group relative inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3 md:py-3.5 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 min-w-[200px] md:min-w-[230px]"
              title="Crear nueva reunión rápidamente"
            >
              <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
              <span>Agregar nueva reunión</span>
            </button>
          </div>
          {/* Botones pequeños abajo */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setOpenCreateEntity(true)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
              title="Agregar nueva entidad"
            >
              <Plus size={16} /> Agregar nueva entidad
            </button>
            <button
              type="button"
              onClick={handleExportToExcel}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
              title="Exportar a Excel"
            >
              <Download size={16} /> Exportar histórico de reuniones
            </button>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
              title="Refrescar"
            >
              <RefreshCcw size={16} /> Refrescar
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="rounded-2xl border border-gray-200 bg-white p-3 md:p-4 shadow-sm space-y-4">
        {/* Primera fila: Búsqueda y filtros básicos */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_180px_180px_180px]">
          <div>
            <label className="block text-xs font-semibold text-[#09274A] mb-1.5 flex items-center gap-1 whitespace-nowrap tracking-wide uppercase">
              <span>Búsqueda</span>
              <TooltipHelper text="Busca entidades externas por nombre, RUT, región o comuna. La búsqueda filtra en tiempo real mientras escribes." />
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, región, comuna…"
                className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A] placeholder:text-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#09274A] mb-1.5 flex items-center gap-1 whitespace-nowrap tracking-wide uppercase">
              <span>Favoritos</span>
              <TooltipHelper text="Filtra entidades marcadas como favoritas. Útil para acceder rápidamente a las entidades más importantes o con las que trabajas frecuentemente." />
            </label>
            <div className="relative">
              <Star className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <select
                value={favoriteFilter}
                onChange={(e) => setFavoriteFilter(e.target.value)}
                className="w-full h-11 pl-10 pr-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
              >
                <option value="">Todos</option>
                <option value="true">Solo favoritos</option>
                <option value="false">Sin favoritos</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#09274A] mb-1.5 flex items-center gap-1 whitespace-nowrap tracking-wide uppercase">
              <span>Tipo</span>
              <TooltipHelper text="Filtra entidades por tipo: Pública (entidades gubernamentales o de acceso público) o Privada (entidades privadas o corporativas)." />
            </label>
            <select
              value={isPublicFilter}
              onChange={(e) => setIsPublicFilter(e.target.value)}
              className="w-full h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
            >
              <option value="">Todos</option>
              <option value="true">Pública</option>
              <option value="false">Privada</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#09274A] mb-1.5 flex items-center gap-1 whitespace-nowrap tracking-wide uppercase">
              <span>Ordenar por reuniones</span>
              <TooltipHelper text="Ordena las entidades por cantidad de reuniones. Mayor a menor: entidades con más reuniones primero. Menor a mayor: entidades con menos reuniones primero." />
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
            >
              <option value="">Sin ordenar</option>
              <option value="desc">Mayor a menor</option>
              <option value="asc">Menor a mayor</option>
            </select>
          </div>
        </div>


        {/* Filtros colapsables de fecha */}
        <div 
          className="border-t border-gray-200"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
          }}
        >
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/50 transition-colors"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)"
            }}
          >
            <div className="flex items-center gap-1.5">
              <Filter size={16} className="text-[#0488AF]" />
              <span className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">
                Filtros de fecha
              </span>
              <TooltipHelper text="Filtra las reuniones por año y/o rango de fechas. Seguirán apareciendo las tarjetas de todos los socios, pero el conteo de reuniones y los niveles de vínculo se ajustarán según el periodo seleccionado. Usa 'Limpiar' para ver todos los datos sin filtros." />
            </div>
            {filtersExpanded ? (
              <ChevronUp size={16} className="text-[#0488AF]" />
            ) : (
              <ChevronDown size={16} className="text-[#0488AF]" />
            )}
          </button>

          {filtersExpanded && (
            <div 
              className="p-2 border-t border-gray-200/50"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              }}
            >
              <TimelineFilter
                year={year}
                month={month}
                yearOptions={yearOptions}
                onYearChange={setYear}
                onMonthChange={setMonth}
                yearFrom={yearFrom}
                monthFrom={monthFrom}
                yearTo={yearTo}
                monthTo={monthTo}
                onYearFromChange={setYearFrom}
                onMonthFromChange={setMonthFrom}
                onYearToChange={setYearTo}
                onMonthToChange={setMonthTo}
              />
            </div>
          )}

        </div>
      </div>

      {/* Indicador de filtros activos - siempre visible */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="text-sm font-semibold text-blue-900">Filtro de fecha aplicado:</span>
          {yearFrom && monthFrom && yearTo && monthTo ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
              Rango: {monthFrom}/{yearFrom} - {monthTo}/{yearTo}
            </span>
          ) : year ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                Año: {year}
              </span>
              {month && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                  Mes: {month === '1' ? 'Enero' : month === '2' ? 'Febrero' : month === '3' ? 'Marzo' : month === '4' ? 'Abril' : month === '5' ? 'Mayo' : month === '6' ? 'Junio' : month === '7' ? 'Julio' : month === '8' ? 'Agosto' : month === '9' ? 'Septiembre' : month === '10' ? 'Octubre' : month === '11' ? 'Noviembre' : 'Diciembre'}
                </span>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">
              Sin filtros (mostrando todos los años)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const currentYear = String(new Date().getFullYear());
              setYear(currentYear);
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs font-semibold tracking-wide uppercase shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors whitespace-nowrap"
            title="Filtrar solo por el año actual y limpiar otros filtros de fecha"
          >
            <Calendar size={16} />
            Filtrar año actual
          </button>
          <button
            onClick={() => {
              setYear('');
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
              setQ('');
              setIndustry('');
              setIsPublicFilter('');
              setFavoriteFilter('');
              setSortOrder('');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors whitespace-nowrap"
            title="Limpiar todos los filtros para ver todos los datos"
          >
            <XCircle size={16} />
            Limpiar todos
          </button>
        </div>
      </div>

      {/* Top 3 entidades con más reuniones - mismo contenedor tipo banner que "Últimas 3 reuniones", diseño interno tipo CRMIndicators */}
      {top3.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-emerald-400/20 bg-gradient-to-br from-emerald-500/5 via-white to-emerald-500/5">
          <div className="p-4 md:p-5">
            {/* Header simple (Top 3 entidades) */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-emerald-900">Top 3 entidades</h3>
                <p className="text-xs md:text-sm text-emerald-800 mt-0.5">
                  Entidades con mayor número de reuniones en el período seleccionado
                </p>
              </div>
            </div>

            {/* Lista (máx. 3 entidades) con layout tipo CRMIndicators */}
            <div className="px-1 md:px-0 space-y-2.5">
              {top3.map((item, idx) => {
                const entityTypeLabel =
                  item.entity_type === 'fundacion'
                    ? 'Fundación'
                    : item.entity_type === 'corporacion'
                    ? 'Corporación'
                    : 'Otro tipo';
                const publicLabel = item.is_public ? 'Pública' : 'Privada';

                return (
                  <div
                    key={item.id || idx}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 hover:bg-white hover:border-emerald-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full border border-emerald-300 bg-white flex items-center justify-center text-[11px] font-semibold text-emerald-700">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-emerald-900 truncate">
                          {item.name}
                        </div>
                        <div className="text-[11px] text-emerald-800/80 truncate mt-0.5">
                          {entityTypeLabel} • {publicLabel}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-900 whitespace-nowrap">
                        {fmt(item.meetingsCount)} {item.meetingsCount === 1 ? 'reunión' : 'reuniones'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Banner de últimas 3 reuniones */}
      {recentMeetings && recentMeetings.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900">Últimas 3 reuniones</h3>
                    <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                      Reuniones más recientes que has registrado
                    </p>
                  </div>
                </div>
                {/* Lista de reuniones */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {loadingRecent ? (
                    <div className="col-span-3 text-xs text-gray-500 py-1">Cargando...</div>
                  ) : (
                    recentMeetings.slice(0, 3).map((meeting, idx) => {
                      const entityName = meeting.entityName || meeting.entity?.name || meeting.entity?.trade_name || meeting.entity?.business_name || 'Sin nombre';
                      return (
                        <button
                          key={meeting.id || idx}
                          onClick={() => openMeetingFromBanner(meeting)}
                          className="group text-left flex items-center gap-3 px-4 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#0488AF] hover:shadow-md transition-all cursor-pointer"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-sm">
                            <span className="text-sm font-bold text-white">{idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-900 truncate group-hover:text-[#0488AF] transition-colors">
                              {entityName}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <Calendar className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-600 font-medium">
                                {onlyDate(meeting.date)}
                              </span>
                            </div>
                            {meeting.title && (
                              <div className="text-xs text-gray-500 truncate mt-1">
                                {meeting.title}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de lista de entidades */}
      {!loading && !error && filtered.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">Entidades</h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    Listado de entidades externas
                  </p>
                  <div className="inline-flex items-center gap-1.5 rounded-lg border border-[#0488AF33] bg-[#E5F4FA] px-3 py-1.5 text-[11px] font-medium text-[#075985] shadow-sm">
                    <span>
                      Al marcar la estrella, esta entidad será considerada como un{' '}
                      <span className="font-semibold text-[#09274A]">actor principal</span>{' '}
                      dentro de <span className="font-semibold text-[#09274A]">Entidades</span>.
                    </span>
                  </div>
                </div>
              </div>
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/95 border border-[#7cc3dd] shadow-sm">
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98]">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[11px] uppercase tracking-wide text-[#0369A1] font-semibold">
                    Total entidades visibles
                  </span>
                  <span className="text-lg font-extrabold text-[#09274A] tabular-nums">
                    {filtered.length}
                  </span>
                </div>
              </div>
            </div>
            {/* Lista de entidades */}
            <div className="space-y-3">
              {filtered.map((item) => (
                <ListItem
                  key={item.id || `${item.rut}-${item.name}`}
                  item={item}
                  onOpen={openModal}
                  filterYear={year}
                  yearFrom={yearFrom}
                  monthFrom={monthFrom}
                  yearTo={yearTo}
                  monthTo={monthTo}
                  onToggleFavorite={handleToggleFavorite}
                  isRecent={hasRecentMeeting(item)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Estados de carga y error */}
      {loading && (
        <div className="py-14 text-center text-gray-500">Cargando…</div>
      )}
      {error && (
        <div className="py-10 text-center text-rose-700">{error}</div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div className="py-14 text-center text-gray-500">No hay resultados.</div>
      )}

      {/* Modal detalle por entidad -> usa entityStub */}
      <OtherCompanyCRMModal 
        open={open} 
        onClose={closeModal} 
        entityStub={selected}
        initialYear={year}
        initialYearFrom={yearFrom}
        initialMonthFrom={monthFrom}
        initialYearTo={yearTo}
        initialMonthTo={monthTo}
        initialMeetingId={initialMeetingId}
        onClearInitialMeetingId={() => setInitialMeetingId(null)}
        onEdit={handleEdit}
        onEntityUpdated={(updated) => {
          setRaw((prev) => prev.map((e) => (e.id === updated.id ? normalizeEntity(updated) : e)));
          setSelected(normalizeEntity(updated));
          load(); // Reload the list to update counts
        }}
        onMeetingCreated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
        onMeetingUpdated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
        onMeetingDeleted={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
      />

      {/* Modal crear ENTIDAD */}
      {openCreateEntity && (
        <CreateOtherEntityModal
          open={openCreateEntity}
          onClose={() => setOpenCreateEntity(false)}
          onCreated={(entity) => {
            const norm = normalizeEntity(entity || {});
            setRaw((prev) => [norm, ...prev]);
            setSelected(norm);
            setOpen(true);
            setOpenCreateEntity(false);
          }}
        />
      )}

      {/* Modal editar ENTIDAD */}
      {openEditEntity && entityToEdit && (
        <CreateOtherEntityModal
          open={openEditEntity}
          onClose={() => {
            setOpenEditEntity(false);
            setEntityToEdit(null);
          }}
          entityToEdit={entityToEdit}
          onCreated={(entity) => {
            const norm = normalizeEntity(entity || {});
            setRaw((prev) => prev.map((e) => (e.id === norm.id ? norm : e)));
            setOpenEditEntity(false);
            setEntityToEdit(null);
          }}
          onDeleted={(deletedEntity) => {
            // Eliminar de la lista cuando se elimina desde el modal
            setRaw((prev) => prev.filter((e) => e.id !== deletedEntity.id));
            setOpenEditEntity(false);
            setEntityToEdit(null);
            load(); // Recargar para actualizar contadores
          }}
        />
      )}

      {/* Modal de creación rápida de reunión */}
      <QuickCreateMeetingModalExternal
        open={openQuickCreate}
        onClose={() => setOpenQuickCreate(false)}
        onCreated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
        onOpenCreateEntity={() => setOpenCreateEntity(true)}
      />
    </div>
  );
}
